<!-- 記事一覧ページ -->
<script setup lang="ts">
  import { useArticleStore } from "~/stores/ArticleStore";
  import {
    useFilterStore,
    articleFilterValues,
    articleFilters,
    articleSortItems,
    articleSortItemsForOwner,
  } from "~/stores/FilterStore";
  import { PlanInfo } from "~/entities/PlanInfo";

  interface Props {
    planInfo: PlanInfo;
  }
  const localProps = withDefaults(defineProps<Props>(), {
    planInfo: undefined,
  });

  interface Emits {
    (event: "onBack"): void;
  }
  defineEmits<Emits>();

  const filterStore = useFilterStore();
  const articleStore = useArticleStore();

  const isSp = isSpDisplayRef();

  const contents = ref<any[]>([]);
  const emptyTextContent = "コンテンツが提供されていません。";

  // データ取得中のフラグ。通信のみ
  // データ取得ローディング表示フラグ。スクロール判定中も表示する
  const loading = ref(false);
  const page = ref(1);
  const pageSize = ref(20);
  const isLast = ref(false);

  const search = computed({
    get: () => {
      return filterStore.searchWordArticle;
    },
    set: value => {
      filterStore.searchWordArticle = value;
    },
  });

  const contentAreaRef = ref<HTMLElement | null>(null);
  const { height: contentAreaElementHeight } = useElementBounding(contentAreaRef);

  onMounted(() => {
    filterStore.resetWithPlanId(localProps.planInfo.id);
  });

  watch(
    () => {
      return search.value;
    },
    () => {
      fetch(true);
    },
  );

  // データ取得
  const getArticles = async (wait = false) => {
    if (loading.value) return;
    loading.value = true;
    if (wait) {
      // Safari の場合、ウェイトを入れないとスクロールがガタガタになるので調整する (Chrome, Edge は問題なし)
      if (isSafari()) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
    try {
      await filterStore.fetchArticleWithFilter(page.value, pageSize.value, localProps.planInfo.id);
      const data = articleStore.articles?.contents || [];
      contents.value = [...contents.value, ...data];
      if (data.length === pageSize.value) {
        page.value++;
      } else {
        isLast.value = true;
      }
    } finally {
      loading.value = false;
    }
  };

  const load = async ({ done }: any) => {
    if (isLast.value) {
      done("empty");
      return;
    }
    await getArticles(false);
    if (isLast.value) {
      done("empty");
    } else {
      done("ok");
    }
  };

  const fetch = async (reset?: boolean) => {
    if (reset) {
      page.value = 1;
      isLast.value = false;
      contents.value = [];
    }
    if (contents.value.length > 0) {
      return;
    }
    await getArticles();
  };

  const handleChangeFilter = (value: string) => {
    page.value = 1;
    filterStore.articlePage = 1;
    filterStore.setSelectedArticleFilter(value);
    fetch(true);
  };

  const changeSort = (value: string) => {
    if (filterStore.selectedArticleSortItem === value) {
      filterStore.selectedArticleSortOrder = filterStore.selectedArticleSortOrder === "desc" ? "asc" : "desc";
    } else {
      filterStore.selectedArticleSortItem = value;
      filterStore.selectedArticleSortOrder = "desc";
    }
    fetch(true);
  };

  const sortItems = computed(() => {
    return localProps.planInfo.isOwner ? articleSortItemsForOwner : articleSortItems;
  });

  const getContents = (isFeatured: boolean) => {
    return contents.value.filter(content => content.isFeatured === isFeatured);
  };

  defineExpose({
    fetch,
    loading,
  });
</script>

<template>
  <MoleculesContainerMain
    v-model:search="search"
    :show-search="true"
    title="記事一覧"
    svg-icon="assets/images/icon/menu/article.svg"
    @on-back="$emit('onBack')"
  >
    <template #header-btns>
      <!-- フィルター -->
      <v-menu scroll-strategy="close">
        <template #activator="{ props }">
          <v-icon
            v-bind="props"
            icon="$mdiFilter"
            :size="isSp ? 'small' : 'default'"
            :color="filterStore.getSelectedArticleFilter().value !== articleFilterValues.All ? 'primary' : 'default'"
          />
        </template>
        <v-sheet class="bg-monochrome-04">
          <div class="py-2">
            <div
              v-for="item in articleFilters"
              :key="item.value"
              class="d-flex justify-space-between cursor-pointer py-2 px-3"
              @click="handleChangeFilter(item.value)"
            >
              <div class="pr-8">{{ item.title }}</div>
              <div>{{ item.value === filterStore.selectedArticleFilter ? "✔" : "" }}</div>
            </div>
          </div>
        </v-sheet>
      </v-menu>
      <!-- ソート -->
      <MoleculesHeaderSortMenu
        :sort-order="filterStore.selectedArticleSortOrder"
        :selected-value="filterStore.selectedArticleSortItem"
        :sort-items="sortItems"
        @change-sort="changeSort"
      />
    </template>
    <!--セレクションコンテンツ-->
    <div ref="contentAreaRef" class="h-100">
      <div v-if="!loading && contents.length == 0" class="mt-8 mx-3 mx-sm-6" style="margin-bottom: 200px">
        {{ emptyTextContent }}
      </div>
      <!-- contents -->
      <v-infinite-scroll v-else :height="contentAreaElementHeight" class="infinitie-scroll" @load="load">
        <div v-if="getContents(true).length" class="mx-2">
          <div class="d-flex align-center pb-2" :class="{ 'px-1': !isSp }">
            <v-icon icon="$mdiPin" class="mr-1" :size="20" />
            <span class="text-subtitle-1 font-weight-bold mb-1">固定</span>
          </div>
          <div
            v-for="featured in getContents(true)"
            :key="featured.id"
            class="pb-3 text-center"
            :class="{ 'px-2': !isSp }"
          >
            <MoleculesBaseFeaturedCard
              :sp-full-width="true"
              :tags="featured?.tags"
              :image="featured?.thumbnailImage"
              :title="featured?.contentsTitle"
              :is-free="featured?.isFree"
              :to-path="`/plan/${featured.plan?.id}/article/${featured.id}`"
              :publish-status="featured?.publishStatus"
              :loading="loading"
              :publish-date-time="featured?.postDateTime"
              :content-id="featured?.id.toString()"
              :content-type="featured?.contentsType"
              :is-unread="featured?.isUnread"
              :good-count="featured?.goodCount"
              :setting-path="
                featured?.isOwner ? `/plan/${featured.plan?.id}/distribute/article/${featured.id}/edit` : ''
              "
              :document-total-page="0"
              :is-good="featured?.isGood"
              :description="featured?.description"
              :is-bookmark="featured?.isBookmark"
              :sp-height="250"
            />
          </div>
          <v-divider v-if="getContents(true).length && getContents(false).length" class="mx-2 mb-6" />
        </div>

        <div v-for="(content, index) in getContents(false)" :key="index">
          <MoleculesArticleListItem :content="content" />
          <v-divider v-if="content.isFeatured" class="mx-5 my-6" />
        </div>
        <template #empty>
          <div class=""></div>
        </template>
      </v-infinite-scroll>
    </div>
  </MoleculesContainerMain>
</template>
<style lang="scss">
  .infinitie-scroll {
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
</style>
